import { api } from '@goaltree/backend/convex/_generated/api'
import { MAX_CHECKINS_PER_WEEK } from '@goaltree/backend/convex/lib/checkins'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation } from 'convex/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

type Schedule = { days: number[]; time: string }

// Monday-first, as people usually read a week.
const WEEK = [1, 2, 3, 4, 5, 6, 0]

const dayLabel = (d: number, lang: string) =>
  // 2023-01-01 was a Sunday.
  new Date(Date.UTC(2023, 0, 1 + d, 12)).toLocaleDateString(lang, {
    weekday: 'short',
    timeZone: 'UTC',
  })

// Settings → Coach calls: scheduled check-in calls (day chips + time).
export function CheckinSchedule({
  schedule,
  isPro,
  coachName,
  onLocked,
}: {
  schedule: Schedule | undefined
  isPro: boolean
  coachName: string
  onLocked: () => void
}) {
  const { t, i18n } = useTranslation()
  const save = useMutation(api.coachCalls.setSchedule).withOptimisticUpdate(
    (store, { schedule: next }) => {
      const prefs = store.getQuery(api.preferences.getMyPreferences, {})
      if (prefs)
        store.setQuery(
          api.preferences.getMyPreferences,
          {},
          {
            ...prefs,
            coachCheckins: next ?? undefined,
            ...(next ? { coachCallsOn: true } : {}),
          },
        )
    },
  )
  const [time, setTime] = useState(schedule?.time ?? '18:30')
  const days = schedule?.days ?? []

  function apply(next: Schedule | null) {
    if (!isPro) return onLocked()
    void save({ schedule: next }).catch(() => toast.error(t('coachCall.scheduleError')))
  }
  function toggleDay(d: number) {
    const on = days.includes(d)
    if (!on && days.length >= MAX_CHECKINS_PER_WEEK) {
      toast(t('coachCall.scheduleMax'))
      return
    }
    const next = on ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b)
    apply(next.length ? { days: next, time } : null)
  }

  return (
    <div className="flex flex-col gap-2.5 px-4 py-3.5">
      <div>
        <p className="text-sm font-medium">{t('coachCall.scheduleTitle')}</p>
        <p className="text-xs text-muted-foreground">
          {t('coachCall.scheduleDesc', { name: coachName })}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {WEEK.map((d) => {
          const on = days.includes(d)
          return (
            <button
              key={d}
              type="button"
              aria-pressed={on}
              onClick={() => toggleDay(d)}
              className={cn(
                'h-8 min-w-11 cursor-pointer rounded-full border px-2.5 text-xs font-semibold transition-colors',
                on
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted',
              )}
            >
              {dayLabel(d, i18n.language)}
            </button>
          )
        })}
        <input
          type="time"
          aria-label={t('coachCall.scheduleTime')}
          value={time}
          onChange={(e) => setTime(e.target.value)}
          onBlur={() => {
            if (days.length && time && time !== schedule?.time) apply({ days, time })
          }}
          className="ml-1 h-8 rounded-lg border border-border bg-background px-2 text-xs tabular-nums"
        />
      </div>
    </div>
  )
}
